"use client";

/* ————— fol. 4v — Colophon —————
   A colophon is the scribe's closing inscription — the note at the
   very end of a manuscript recording who made it, and why. Ours closes
   the tour: a compact FAQ (the last marginal notes) then the download
   CTA staged as the scribe's sign-off, framed by a gold hairline +
   ornament. All reveals scroll-scrubbed via <Scrub> (Jay direction,
   2026-07-10) — progress tracks the scrollbar in both directions.
*/

import { useState } from "react";
import Link from "next/link";
import { Scrub } from "./Scrub";

const APP_STORE_URL = "https://apps.apple.com/us/app/sola-bible-app/id6739747733";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.sola.bible";

const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: "What makes Sola different from other Bible apps?",
    a: "Sola pairs traditional study depth (Greek, Hebrew, historical context) with an AI study companion that cites its sources instead of paraphrasing them. Scripture stays the primary text; Sola explains it, it doesn't replace it.",
  },
  {
    q: "Is Sola free to use?",
    a: "Yes. Reading, note-taking, and search are free. A subscription unlocks unlimited AI study questions and deeper original-language tools.",
  },
  {
    q: "How does the search feature work?",
    a: "Search runs against the full text and trusted study resources. It surfaces verses, context, and word meanings, and always grounds the answer in Scripture rather than speculation.",
  },
  {
    q: "Can I use Sola offline?",
    a: "Yes. Download translations and your notes for offline reading. Search and AI study tools need a connection.",
  },
  {
    q: "What Bible translations are available?",
    a: "Sola currently supports the ASV, with ESV, NIV, NASB, NLT, and KJV rolling out based on licensing and user demand.",
  },
];

function FAQItem({
  item,
  isOpen,
  onToggle,
}: {
  item: { q: string; a: string };
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <Scrub from="up" start="top 96%" end="top 78%" className="border-b border-border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-6 py-5 text-left"
      >
        <span className="font-medium text-[0.95rem] sm:text-base text-foreground">{item.q}</span>
        <span
          aria-hidden="true"
          className="relative shrink-0 h-4 w-4 text-accent transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ transform: isOpen ? "rotate(45deg)" : "rotate(0deg)" }}
        >
          <span className="absolute left-1/2 top-1/2 h-[1px] w-4 -translate-x-1/2 -translate-y-1/2 bg-accent" />
          <span className="absolute left-1/2 top-1/2 h-4 w-[1px] -translate-x-1/2 -translate-y-1/2 bg-accent" />
        </span>
      </button>
      <div
        className="overflow-hidden"
        style={{
          display: "grid",
          gridTemplateRows: isOpen ? "1fr" : "0fr",
          transition: "grid-template-rows 320ms cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <div className="min-h-0 overflow-hidden">
          <p className="max-w-2xl pb-5 text-sm sm:text-[0.95rem] leading-relaxed text-muted-foreground">
            {item.a}
          </p>
        </div>
      </div>
    </Scrub>
  );
}

export default function Colophon() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section
      id="download"
      data-folio="fol. 4v"
      data-folio-title="Colophon"
      className="relative border-t border-border"
    >
      {/* ————— FAQ ————— */}
      <div className="container pt-28 sm:pt-32 pb-8">
        <Scrub from="up">
          <p className="rubric mb-4">Colophon</p>
          <h2 className="max-w-2xl text-balance mb-10 sm:mb-14">
            A few last questions, before the final page.
          </h2>
        </Scrub>

        <div className="max-w-3xl border-t border-border">
          {FAQ_ITEMS.map((item, i) => (
            <FAQItem
              key={item.q}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>

      {/* ————— The colophon proper ————— */}
      <div className="relative border-t border-border py-28 sm:py-36">
        <div className="container flex flex-col items-center text-center">
          {/* Gold hairline ornament — ✦ between two rules */}
          <Scrub from="up" start="top 94%" end="top 74%" className="mb-9 flex items-center gap-4">
            <span className="h-px w-14 sm:w-20 bg-gradient-to-r from-transparent to-[rgba(212,163,62,0.65)]" />
            <span className="text-[color:var(--accent)] text-lg leading-none">&#10022;</span>
            <span className="h-px w-14 sm:w-20 bg-gradient-to-l from-transparent to-[rgba(212,163,62,0.65)]" />
          </Scrub>

          <Scrub from="up" start="top 92%" end="top 66%">
            <h2 className="font-scripture italic font-normal text-balance max-w-3xl mb-6 text-[2.25rem] sm:text-5xl md:text-6xl leading-[1.08]">
              Here ends the tour.
              <br />
              The text is waiting.
            </h2>
          </Scrub>

          <Scrub from="up" start="top 94%" end="top 72%">
            <p className="text-lg text-muted-foreground max-w-md mb-10">
              Free to download. Scripture-first, always.
            </p>
          </Scrub>

          <Scrub from="up" start="top 96%" end="top 76%" className="flex flex-col sm:flex-row gap-4 mb-8">
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
              Download for iOS
            </a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-lg">
              Download for Android
            </a>
          </Scrub>

          <Scrub from="none" start="top 98%" end="top 80%">
            <Link
              href="/bible-verse-explained"
              className="text-sm text-muted-foreground border-b border-transparent pb-0.5 hover:text-foreground hover:border-[color:var(--accent)] transition-colors"
            >
              Or understand a verse on the web first.
            </Link>
          </Scrub>
        </div>
      </div>
    </section>
  );
}
